import {
  Button,
  CheckIcon,
  Checkbox,
  Group,
  LoadingOverlay,
  Modal,
  Space,
  Stack,
  Text,
  TextInput,
  rem,
} from '@mantine/core';
import { useApplicantFormContext } from '../ApplicantFormPage';
import { RadioButton } from '../../List/Components/RadioButton';
import { useState } from 'react';
import { mapState } from '../Functions/helperFunctions';
import { notifications } from '@mantine/notifications';
import { getFetcher, postFetcher, putFetcher } from '@/api/fetchers';
import { IconX } from '@tabler/icons-react';
import {
  applicantToCreateClient,
  appllicantToCreateClientCarePlan,
} from '@/schemas/converters/clientSchemasConverter';
import { displayError } from '@/utils/error/errorHandler';
import api from '@/api/api';
import useSWR from 'swr';
import { TiTick } from 'react-icons/ti';

interface ProcessModalProps {
  opened: boolean;
  close: () => void;
}

interface Reason {
  id: number;
  name: string;
}

type ApplicantState =
  | 'ACTIVE'
  | 'INACTIVE'
  | 'QUEUED'
  | 'WAITING'
  | 'CANCELLED';

type ProcessState = 'activate' | 'deactivate' | 'cancel' | 'to-queue' | '';

export const ProcessModal: React.FC<ProcessModalProps> = props => {
  const form = useApplicantFormContext();
  const applicant = form.values;
  const [newState, setNewState] = useState<ProcessState>('');

  const title = `Aktuální stav - ${mapState(applicant.currState)}`;

  const handleClose = () => {
    setNewState('');
    props.close();
  };
  const updateState = async (
    state: ApplicantState,
    cancelReasons?: number[],
    cancelNote?: string
  ) => {
    try {
      const payload: any = { state };

      if (cancelReasons) {
        payload.cancelReasons = cancelReasons;
      }

      if (cancelNote) {
        payload.cancelNote = cancelNote;
      }

      await putFetcher(`/applicants/${form.values.id}/state`, payload);
      notifications.show({
        message: `Stav zájemce byl úspěšně změněn.`,
        color: 'green',
        icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
      });
      form.setFieldValue('currState', state);
      handleClose();
    } catch (error) {
      displayError(error);
    }
  };

  const createClient = async (): Promise<boolean> => {
    const validationResult = form.validate();

    if (validationResult.hasErrors) {
      console.error('Form failed validation:', validationResult.errors);
      notifications.show({
        title: `Klienta nebylo možné vytvořit.`,
        message: `Formulář obsahuje chyby.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return false;
    }

    try {
      if (applicant.clientId == null) {
        // create new client
        const clientCreate = applicantToCreateClient(form.values);
        const newClient = await postFetcher(`/clients`, clientCreate);

        // add carePlan to new client
        const carePlan = appllicantToCreateClientCarePlan(form.values);
        await postFetcher(`/clients/${newClient.id}/careplans`, carePlan);

        notifications.show({
          message: `Klient byl úspěšně vytvořen.`,
          color: 'green',
          icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
        });

        const baseUrl =
          window.location.origin +
          window.location.pathname.split('/').slice(0, -2).join('/');
        const url = `${baseUrl}/clients/${newClient.id}/personal`;
        window.open(url, '_blank');
      } else {
        // add carePlan to existing client
        const carePlan = appllicantToCreateClientCarePlan(form.values);
        await postFetcher(`/clients/${applicant.clientId}/careplans`, carePlan);

        notifications.show({
          message: `Klient byl úspěšně vytvořen.`,
          color: 'green',
          icon: <CheckIcon style={{ width: rem(20), height: rem(20) }} />,
        });

        window.open(`/clients/${applicant.clientId}/personal`, '_blank');
      }
      return true;
    } catch (err) {
      displayError(err);
      return false;
    }
  };

  const executeProcess = async () => {
    switch (newState) {
      case 'activate':
        if (await createClient()) {
          void updateState('ACTIVE');
        }
        break;
      case 'deactivate':
        deactivateClient();
        break;
      case 'cancel':
        cancelClient();
        break;
      case 'to-queue':
        void updateState('QUEUED');
        break;
      default:
        if (
          applicant.currState !== 'CANCELLED' &&
          applicant.currState !== 'INACTIVE'
        ) {
          notifications.show({
            message: `Není vybrán žádný proces.`,
            color: 'red',
            icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
          });
        }
        handleClose();
        break;
    }
  };

  const generateContent = () => {
    switch (applicant.currState) {
      case 'ACTIVE':
        return (
          <Stack style={{ margin: '10px' }}>
            <Group justify="space-between">
              <Text>Zneaktivnit klienta</Text>
              <RadioButton
                selected={newState === 'deactivate'}
                onClick={() => setNewState('deactivate')}
              />
            </Group>

            <Group justify="space-between">
              <Text>Stornovat klienta</Text>
              <RadioButton
                selected={newState === 'cancel'}
                onClick={() => setNewState('cancel')}
              />
            </Group>
          </Stack>
        );
      case 'CANCELLED':
        return <Text>Stav nelze měnit</Text>;
      case 'INACTIVE':
        return <Text>Stav nelze měnit</Text>;
      case 'QUEUED':
        return (
          <Stack style={{ margin: '10px' }}>
            <Group justify="space-between">
              <Text>Zaktivnit klienta podle formuláře</Text>
              <RadioButton
                selected={newState === 'activate'}
                onClick={() => setNewState('activate')}
              />
            </Group>

            <Group justify="space-between">
              <Text>Stornovat klientův formulář</Text>
              <RadioButton
                selected={newState === 'cancel'}
                onClick={() => setNewState('cancel')}
              />
            </Group>

            <Group justify="space-between">
              <Text>Zneaktivnit klienta</Text>
              <RadioButton
                selected={newState === 'deactivate'}
                onClick={() => setNewState('deactivate')}
              />
            </Group>
          </Stack>
        );
      case 'WAITING':
        return (
          <Stack style={{ margin: '10px' }}>
            <Group justify="space-between">
              <Text>Zaktivnit klienta podle formuláře</Text>
              <RadioButton
                selected={newState === 'activate'}
                onClick={() => setNewState('activate')}
              />
            </Group>

            <Group justify="space-between">
              <Text>Stornovat klientův formulář</Text>
              <RadioButton
                selected={newState === 'cancel'}
                onClick={() => setNewState('cancel')}
              />
            </Group>

            <Group justify="space-between">
              <Text>Přesunout do pořadníku</Text>
              <RadioButton
                selected={newState === 'to-queue'}
                onClick={() => setNewState('to-queue')}
              />
            </Group>
          </Stack>
        );
      default:
        return <Text>Nepodařilo se načíst aktuální stav</Text>;
    }
  };

  const [note, setNote] = useState<string>('');

  const deactivateClient = async () => {
    if (note.length === 0) {
      notifications.show({
        message: `Je nutné vyplnit poznámku.`,
        color: 'red',
        icon: <IconX style={{ width: rem(20), height: rem(20) }} />,
      });
      return;
    }

    try {
      await updateState('INACTIVE', undefined, note);
    } catch (error) {
      displayError(error);
    }
  };

  const [selectedReasonIds, setSelectedReasonIds] = useState<number[]>([]);
  const [loading] = useState(false);
  const { values: cancelApplicant, setFieldValue } = useApplicantFormContext();
  const { data: availableReasons } = useSWR<Reason[]>(
    '/applicants/cancel-reasons',
    getFetcher
  );

  const handleCheckboxChange = (reasonId: number, isChecked: boolean) => {
    setSelectedReasonIds(current =>
      isChecked ? [...current, reasonId] : current.filter(id => id !== reasonId)
    );
  };

  const cancelClient = async () => {
    if (selectedReasonIds.length === 0) {
      notifications.show({
        message: 'Musíte vybrat alespoň jeden důvod storna/zamítnutí.',
        color: 'red',
        icon: <IconX size={20} />,
      });
      return;
    }

    try {
      const stateToUpdate = 'CANCELLED';
      const updatedApplicant = {
        cancelReasons: selectedReasonIds,
        cancelNote: note === '' ? null : note,
        state: stateToUpdate,
      };

      await api.put(
        `/applicants/${cancelApplicant.id}/state`,
        updatedApplicant
      );
      notifications.show({
        message: 'Změny byly úspěšně uloženy.',
        color: 'green',
        icon: <TiTick style={{ width: rem(20), height: rem(20) }} />,
      });
      setFieldValue(
        'cancelReasons',
        selectedReasonIds.map(id => id.toString())
      );
      setFieldValue('cancelNote', note);
      form.setFieldValue('currState', stateToUpdate);
      handleClose();
    } catch (error) {
      displayError(error);
    }
  };

  function generateConfirmation() {
    if (newState === 'deactivate') {
      return (
        <TextInput
          label="Poznámka"
          value={note}
          onChange={event => setNote(event.currentTarget.value)}
        />
      );
    } else if (newState === 'cancel') {
      return (
        <Stack style={{ height: '35vh' }} justify="space-between">
          <LoadingOverlay visible={loading} />
          {availableReasons?.map(reason => (
            <Checkbox
              key={reason.id}
              label={reason.name}
              checked={selectedReasonIds.includes(reason.id)}
              onChange={event =>
                handleCheckboxChange(reason.id, event.currentTarget.checked)
              }
            />
          ))}
          <TextInput
            label="Poznámka"
            value={note}
            onChange={event => setNote(event.target.value)}
          />
        </Stack>
      );
    }
  }

  return (
    <Modal
      opened={props.opened}
      onClose={handleClose}
      title={title}
      centered
      size={'lg'}
    >
      <Stack justify="space-between">
        {generateContent()}
        {generateConfirmation()}
        <Space h="xl" />
        <Group justify="space-between" grow>
          <Button variant="outline" onClick={handleClose}>
            Zrušit
          </Button>
          <Button
            onClick={() => {
              void executeProcess();
            }}
          >
            Pokračovat
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
};
